/// <amd-module name="Release/Commands/Dialogs/PipelineDialogWithComment" />
import * as React from "react";

import { Dialog } from "VSSUI/Dialog";
import { Button } from "VSSUI/Button";
import { css } from "VSSUI/Util";
import { TextField } from "VSSUI/TextField";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { VssComponent, IVssComponentProperties, IVssComponentContext, IVssLayoutManager, Components } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";

import * as Resources from "Release/Commands/Dialogs/Resources";

import "VSS/LoaderPlugins/Css!Release/Commands/PipelineDialogWithComment";

interface IPipelineDialogWithCommentProps<T> extends IVssComponentProperties {
    dialogTitle: string;
    commentBoxTitle: string;
    onClose: () => void;
    onOkClick: (comment: string) => Promise<T> | void;
    okDisabled?: boolean;
    footerInfoMessage?: string;
}

interface IPipelineDialogWithCommentState {
    comment: string;
    error: string;
}

class PipelineDialogWithComment<T> extends VssComponent<IPipelineDialogWithCommentProps<T>, IPipelineDialogWithCommentState>{
    constructor(props: IPipelineDialogWithCommentProps<T>, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            comment: "",
            error: ""
        };
    }

    public render(): React.ReactNode {
        return <Dialog
            titleProps={{ text: this.props.dialogTitle }}
            onDismiss={this._onDialogClose}
            defaultActiveElement=".comment-text-field"
            escDismiss={true}
            calloutContentClassName={css("pipeline-comment-dialog-container", this.props.className)}
            footerButtonProps={[
                {
                    onClick: this._onDialogClose,
                    text: Resources.Cancel
                },
                {
                    onClick: this._onOkClicked,
                    disabled: this.props.okDisabled,
                    primary: true,
                    text: Resources.Ok
                }
            ]}>
            <div className={"flex-grow"}>
                {
                    this.state.error &&
                    <MessageBar severity={MessageBarSeverity.Error}>
                        {this.state.error}
                    </MessageBar>
                }
                {this.props.children}
                <TextField
                    inputClassName={"comment-text-field"}
                    value={this.state.comment}
                    label={this.props.commentBoxTitle}
                    multiline={true}
                    rows={3}
                    onChange={this._onCommentChange}
                />
                {
                    this.props.footerInfoMessage &&
                    <div role="alert">
                        <MessageBar
                            severity={MessageBarSeverity.Info}>
                            {this.props.footerInfoMessage}
                        </MessageBar>
                    </div>
                }
            </div>
        </Dialog >;
    }

    private _closeDialog() {
        if (!!this.props.onClose) {
            this.props.onClose();
        }
    }

    private _onCommentChange = (event: React.ChangeEvent<HTMLInputElement>, comment: string) => {
        this.setState({ comment: comment });
    }

    private _onDialogClose = (): void => {
        this._closeDialog();
    }

    private _onOkClicked = (): void => {
        if (!!this.props.onOkClick) {
            this.setState({ error: "" });
            const onOkPromise = this.props.onOkClick((this.state && this.state.comment) || "");
            if (onOkPromise && typeof onOkPromise.then === "function") {
                onOkPromise.then(() => {
                    this._closeDialog();
                },
                    (error) => {
                        this.setState({ error: (error.message || error) as string });
                    });
            }
            else {
                this._closeDialog();
            }
        }
    }
}

Components.add("rel-pipe-comment-dialog", PipelineDialogWithComment);
